<script setup lang="ts">
  import { ref } from "vue";
  import { useRouter } from "vue-router";
  import { Modal, Avatar, IconExit, Text, IconSettings, IconInfo, IconEdit } from "@ui-kit";
  import { SessionStorageService } from "@src/services";
  import { useProfileOptions } from "@src/hooks";
  import { useUserInfoStore, useSettingsStore } from "@src/store";
  import { userLocationDictionary } from "@src/dictionary";
  import { default as MainPageUserLocationModal } from "./MainPageUserLocationModal.vue";

  const emit = defineEmits<{ "close": [value: void] }>();

  const isLocationModalVisible = ref(false);

  const { push } = useRouter();
  const { profile, clearProfile } = useProfileOptions();
  const userInfoStore = useUserInfoStore();
  const settingsStore = useSettingsStore();

  const gotoDeviceInfo = () => push("/device-info");
  const gotoSettings = () => push("/settings");

  const handleLogout = () => {
    SessionStorageService.removeItem("token");
    clearProfile();

    window.location.reload();
  };
  const handleChangeLocation = (location: string, store_types: string[]) => {
    settingsStore.setLocation(location);
    settingsStore.setStoreTypes(store_types);

    isLocationModalVisible.value = false;
  };
</script>

<template>
  <Modal close grabber @onClose="emit('close')">
    <template #header>
      <div class="user-info_header">
        <div class="user-info_block">
          <Avatar
            class="user-info_avatar"
            :first-name="profile?.current_user.first_name"
            :last-name="profile?.current_user.last_name"
          />
          <div class="user-info_description">
            <Text font="body-medium-16" :wrap="false" data-testid="user-info-modal-user-name">
              {{ profile?.current_user.first_name }}
            </Text>
            <Text font="body-regular-14" color="secondary" :wrap="false" data-testid="user-info-modal-user-shop">
              {{ userInfoStore.shop?.title || "..." }}
            </Text>
          </div>
        </div>
        <div
          class="user-info_location"
          @click="isLocationModalVisible = true"
          data-testid="user-info-modal-location-btn"
        >
          <Text font="body-medium-14" color="success">{{ userLocationDictionary[settingsStore.location] }}</Text>
          <IconEdit style="width: 20px; height: 20px" />
        </div>
      </div>
    </template>
    <div class="user-info_actions">
      <div class="user-info_action" @click="gotoSettings" data-testid="user-info-modal-settings-btn">
        <IconSettings />
        <Text>{{ $t("Настройки приложения") }}</Text>
      </div>
      <div class="user-info_action" @click="gotoDeviceInfo" data-testid="user-info-modal-device-info-btn">
        <IconInfo />
        <Text>{{ $t("Об устройстве") }}</Text>
      </div>
      <div class="user-info_action" @click="handleLogout" data-testid="user-info-modal-logout-btn">
        <IconExit />
        <Text color="error">{{ $t("Выйти из аккаунта") }}</Text>
      </div>
    </div>
  </Modal>
  <MainPageUserLocationModal
    v-if="isLocationModalVisible"
    :shop="userInfoStore.shop"
    :location="settingsStore.location"
    :store_types="settingsStore.store_types"
    @close="isLocationModalVisible = false"
    @click:apply="handleChangeLocation"
  />
</template>

<style scoped>
  .user-info_header {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .user-info_block {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .user-info_block .user-info_avatar {
    flex-shrink: 0;
  }

  .user-info_block .user-info_description {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .user-info_block .user-info_description span {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .user-info_block .user-info_description span:first-of-type {
    padding-right: 30px;
  }

  .user-info_actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .user-info_actions .user-info_action {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0;
  }

  .user-info_actions .user-info_action:last-of-type svg {
    stroke: var(--sf-base-icon-error);
  }

  .user-info_location {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 12px;
    background-color: var(--sf-base-background-success);

    &::before {
      content: "";
      background-color: var(--sf-toast-text-success);
      width: 4px;
      height: 4px;
      border-radius: 50%;
    }

    & :deep(.text) {
      color: var(--sf-toast-text-success);
    }

    & :deep(.icon) {
      margin-left: auto;
    }

    & :deep(.icon path) {
      stroke: var(--sf-toast-text-success);
    }
  }
</style>
